<template>
  <div
    class="bingo-cell"
    :class="cellClasses"
    @click="onClick"
  >
    <div v-if="isFree" class="bingo-cell__free">
      <icon fill="#FFC107">Star</icon>
      <span class="text-white">FREE</span>
    </div>
    <template v-else>
      <template v-if="boardMode === 'places'">
        <template v-if="cell.marked && placeIconUrl">
          <div
            class="bingo-cell__label bingo-cell__label--category bingo-cell__label--image-only"
            :style="placeIconStyle"
            role="img"
            :aria-label="`${displayVenueName} — checked in`"
          />
        </template>
        <template v-else>
          <div class="bingo-cell__label">
            {{ displayVenueName }}
          </div>
        </template>
      </template>
      <template v-else>
        <template v-if="cell.marked && isCategoryImageMode">
          <div
            class="bingo-cell__label bingo-cell__label--category bingo-cell__label--image-only"
            :style="categoryImageStyle"
            role="img"
            :aria-label="displayCategoryName"
          />
        </template>

        <template v-else-if="cell.marked && categoryIconUrl">
          <div
            class="bingo-cell__label bingo-cell__label--category bingo-cell__label--image-only"
            :style="categoryIconStyle"
          />
        </template>

        <template v-else-if="cell.marked && !categoryIconUrl">
          <div
            class="bingo-cell__label bingo-cell__label--category"
            :class="{ 'bingo-cell__label--with-icon': categoryIconUrl }"
            :style="categoryIconStyle"
          >
            <span class="bingo-cell__label-text">{{ displayCategoryName }}</span>
          </div>
        </template>

        <template v-else>
          <div
            class="bingo-cell__label bingo-cell__label--category"
          >
            <span class="bingo-cell__label-text">{{ displayCategoryName }}</span>
          </div>
        </template>
        <div
          v-if="showCategoryAvailabilityOverlay"
          class="bingo-cell__availability-overlay"
        >
          <icon fill="error" :height="16">Lock</icon>
          <span
            v-if="categoryAvailabilityOverlayDateLabel"
            class="bingo-cell__availability-date"
          >
            {{ categoryAvailabilityOverlayDateLabel }}
          </span>
        </div>
      </template>
      <div v-if="showCheck" class="bingo-cell__check">
        <v-icon icon="mdi-check" size="10"></v-icon>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatNextAvailableMessage, getNextAvailableDateTime, lookupCategoryActiveDatesEntry } from '@/utils/dateRanges';

export default {
  name: 'BingoCell',
  props: {
    cell: {
      type: Object,
      required: true
    },
    row: {
      type: Number,
      required: true
    },
    col: {
      type: Number,
      required: true
    },
    boardMode: {
      type: String,
      default: 'places'
    },
    isCenter: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click-cell'],
  computed: {
    ...mapGetters('venues', { findVenue: 'find' }),
    ...mapGetters('categories', { findCategory: 'find' }),
    ...mapGetters('bingo', ['getCategoryAvailability']),
    ...mapGetters('bingo', { categoryActiveDates: 'categoryActiveDates' }),
    placeIconUrl() {
      return this.$store.state.bingo?.placeIconUrl ?? null;
    },
    placeIconStyle() {
      if (!this.placeIconUrl) return {};
      return {
        backgroundImage: `url(${this.placeIconUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat'
      };
    },
    categoryDisplayMode() {
      return this.$store.state.bingo?.categoryDisplayMode ?? 'text';
    },
    categoryImageUrl() {
      const id = this.cell?.category_id;
      if (id == null) return null;
      const urls = this.$store.state.bingo?.categoryImageUrls ?? {};
      return urls[id] ?? null;
    },
    isCategoryImageMode() {
      return (
        this.boardMode === 'categories' &&
        this.categoryDisplayMode === 'image' &&
        !!this.categoryImageUrl
      );
    },
    categoryImageStyle() {
      if (!this.categoryImageUrl) return {};
      return {
        backgroundImage: `url(${this.categoryImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    },
    categoryFromStore() {
      const id = this.cell?.category_id;
      if (id == null) return null;
      return this.findCategory(id) ?? null;
    },
    categoryIconUrl() {
      if (this.isCategoryImageMode) return this.categoryImageUrl;
      if (this.categoryFromStore?.media) {
        const media = this.categoryFromStore.media;
        const icon = Array.isArray(media)
          ? media.find(m => (m.location === 'Icon' || m.name === 'Icon'))
          : null;
        if (icon?.url) return icon.url;
      }
      const venueIds = this.cell?.category_venue_ids;
      if (!Array.isArray(venueIds) || venueIds.length === 0) return null;
      const venue = this.findVenue(venueIds[0]);
      const categories = venue?.categories;
      if (!Array.isArray(categories)) return null;
      const categoryName = this.cell?.category_name;
      const category = categories.find(c => c?.name === categoryName);
      const media = category?.media;
      if (!Array.isArray(media)) return null;
      const icon = media.find(m => (m.location === 'Icon' || m.name === 'Icon'));
      return icon?.url ?? null;
    },
    categoryIconStyle() {
      if (!this.categoryIconUrl) return {};
      return {
        backgroundImage: `url(${this.categoryIconUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat'
      };
    },
    isFree() {
      //return this.cell?.type === 'free' || this.isCenter;
      return this.cell?.type === 'free';
    },
    categoryAvailability() {
      if (
        this.boardMode !== 'categories' ||
        this.cell?.type !== 'category' ||
        this.cell?.category_id == null
      ) {
        return {
          isAvailable: true,
          isBlackout: false,
          nextAvailableDate: null
        };
      }
      return this.getCategoryAvailability(this.cell.category_id);
    },
    isDisabled() {
      if (this.boardMode !== 'categories' || this.isFree) {
        return false;
      }
      if (this.cell?.type !== 'category' || this.cell?.category_id == null) {
        return false;
      }
      return !this.categoryAvailability.isAvailable;
    },
    showCategoryAvailabilityOverlay() {
      return (
        this.boardMode === 'categories' &&
        this.cell?.type === 'category' &&
        !this.isFree &&
        !this.cell?.marked &&
        this.isDisabled
      );
    },
    categoryAvailabilityOverlayDateLabel() {
      if (!this.showCategoryAvailabilityOverlay) return '';

      const entry = lookupCategoryActiveDatesEntry(
        this.$store.state.bingo?.categoryActiveDates,
        this.cell?.category_id
      );
      if (!entry) return '';

      const next = getNextAvailableDateTime(
        entry.active_dates || [],
        entry.blackout_dates || [],
        new Date()
      );
      if (!next) return '';

      return formatNextAvailableMessage(next);
    },
    isEffectivelyDisabled() {
      return this.disabled || this.isDisabled;
    },
    cellClasses() {
      return {
        'bingo-cell--free': this.isFree,
        'bingo-cell--marked': this.cell?.marked && !this.isFree,
        'bingo-cell--clickable':
          !this.isFree && !this.isEffectivelyDisabled,
        'bingo-cell--disabled': this.disabled,
        'bingo-cell--category-unavailable': this.isDisabled && !this.disabled
      };
    },
    venueName() {
      if (this.cell?.type !== 'venue' || !this.cell?.venue_id) return '';
      const venue = this.findVenue(this.cell.venue_id);
      return venue?.name ?? `Venue ${this.cell.venue_id}`;
    },
    categoryName() {
      if (this.categoryFromStore?.name) return this.categoryFromStore.name;
      return this.cell?.category_name ?? '';
    },
    categoryVenueCount() {
      const ids = this.cell?.category_venue_ids;
      return Array.isArray(ids) ? ids.length : null;
    },
    displayVenueName() {
      const name = this.venueName;
      
      const maxLength = 60;
      return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
    },
    displayCategoryName() {
      const name = this.categoryName;
      const maxLength = 60;
      return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
    },
    showCheck() {
      if (!this.cell?.marked) return false;
      if (this.boardMode === 'places') return !this.placeIconUrl;
      if (this.isCategoryImageMode) return true;
      return !this.categoryIconUrl;
    }
  },
  methods: {
    onClick() {
      if (this.isFree || this.isEffectivelyDisabled) return;
      this.$emit('click-cell', {
        row: this.row,
        col: this.col,
        cell: this.cell
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.bingo-cell {
  aspect-ratio: 1;
  border: 1px solid rgb(var(--v-theme-grey-lighten-1));
  border-radius: 12px;
  container-type: inline-size;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  position: relative;
  min-height: 0;
  text-align: center;

  &--free {
    background-color: rgb(var(--v-theme-primary));
    font-weight: 700;
    color: rgb(var(--v-theme-on-surface));
  }

  &--marked {
    background-color: rgb(var(--v-theme-primary));
    color: white;
    &.bingo-cell--clickable:hover::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: rgb(var(--v-theme-grey-lighten-3));
      mix-blend-mode: multiply;
      pointer-events: none;
    }
  }

  &--clickable {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgb(var(--v-theme-grey-lighten-3));
    }
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.5;
    pointer-events: none;
  }

  &--category-unavailable {
    opacity: 0.72;
    background-color: rgba(var(--v-theme-grey-lighten-2), 0.65) !important;
    border-color: rgba(var(--v-theme-grey-lighten-1), 0.9) !important;
  }

  &__availability-overlay {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 4px;
    background: rgba(255, 255, 255, 0.75);
    border-radius: inherit;
    pointer-events: none;
    border: 2px solid rgba(var(--v-theme-error), 0.95);
  }

  &__availability-date {
    font-size: 6.5px;
    line-height: 1.1;
    font-weight: 600;
    color: rgb(var(--v-theme-error));
    text-align: center;
  }

  &__label {
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1.2;
    word-break: keep-all;
  }

  &__label--category {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    min-height: 0;
    padding: 2px;

    &.bingo-cell__label--with-icon {
      padding-top: 4px;
      padding-bottom: 2px;
    }

    .bingo-cell__label-text {
      flex-shrink: 0;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      text-shadow: 0 0 2px rgba(255, 255, 255, 0.8);
    }
  }

  &__count {
    font-size: 0.6rem;
    opacity: 0.9;
    margin-top: 2px;
  }

  &__check {
    position: absolute;
    bottom: 2px;
    right: 2px;
  }
}

.bingo-cell.bingo-cell--marked {
    background-color: rgb(var(--v-theme-primary)) !important;
}

.bingo-cell__check {
    bottom: unset !important;
    top: 0.125rem !important;
    right: 0.125rem !important;
}

.bingo-cell__label {
    padding-left: .125rem;
    padding-right: .125rem;
    font-size: clamp(6px, 13cqi, 8.45px) !important;
    color: #1b2632;
    /* font-weight: 500 !important; */
}

.bingo-cell.bingo-cell--marked .bingo-cell__label,
.bingo-cell.bingo-cell--marked .bingo-cell__label-text {
    color: #fff;
}

.bingo-cell.bingo-cell--clickable {
    border-color: #dae0e7;
    border-radius: 10px !important;
}

.bingo-board__row {
    gap: .25rem !important;
    margin-bottom: 0.25rem !important;
}

.bingo-cell__check {
    background-color: #f99810;
    width: .875rem !important;
    height: .875rem !important;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 9999px;
}

.bingo-cell.bingo-cell--marked {border-radius: 10px !important;}

.bingo-cell.bingo-cell--free {
    background: rgb(var(--v-theme-primary));
    font-size: .75rem;
    line-height: 1rem;
}

.bingo-cell__free {
    display: flex;
    align-items: center;
    font-size: clamp(7px, 18cqi, 12px);
}

.bingo-cell__free span {
    font-size: inherit;
    line-height: 1.3;
}

.bingo-cell__free :deep(svg) {
    padding: 0 !important;
    width: 1.5em;
    height: 1.5em;
    padding-right: 1px !important;
}

/* categories */
.bingo-cell__label--category.bingo-cell__label {
    height: 100%;
    width: 100%;
    padding: 4px !important;
    border-radius: 8px;
    position: relative;
}

.bingo-cell__label--category .bingo-cell {
    height: 100%;
}

.bingo-cell__label--category span.bingo-cell__label-text {
    margin: auto;
    display: block;
    align-items: center;
    background-color: #ffffffcc;
    padding-left: .25rem;
    padding-right: .25rem;
    padding-top: .125rem;
    padding-bottom: .125rem;
    position: absolute;
    width: 90%;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 0.25rem;
    z-index: 1;
    color: #1b2632 !important;
}


.bingo-cell.bingo-cell--marked:has(.bingo-cell__label--category) {
    border-color: rgb(var(--v-theme-primary)) !important;
    border-width: 2px !important;
    padding: 0 !important;
}
.bingo-cell:has(.bingo-cell__label--category) {
    padding: 0 !important;
}
.bingo-cell.bingo-cell--marked .bingo-cell__label--category.bingo-cell__label::before {
    position: absolute;
    content: "";
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: rgba(0,0,0,0.5);
    border-radius: 8px;
}

.bingo-cell__label--image-only {
  padding: 0 !important;

  &::before {
    display: none;
  }
}
</style>
